const DEFAULT_FRONTEND_ORIGIN = 'http://localhost:3001'

/**
 * Configured Next.js `basePath` (e.g. `/carefront`), or empty when the app is
 * served at the origin root.
 */
export function getBasePath(): string {
    const raw = process.env.NEXT_PUBLIC_BASE_PATH?.trim() ?? ''
    if (!raw || raw === '/') return ''
    const withLeadingSlash = raw.startsWith('/') ? raw : `/${raw}`
    return withLeadingSlash.replace(/\/$/, '')
}

/**
 * Public site origin from `NEXT_PUBLIC_FRONTEND_URL` (path segment ignored).
 * Mount path belongs in `NEXT_PUBLIC_BASE_PATH`, not this value.
 */
export function getPublicFrontendOrigin(fallback: string = DEFAULT_FRONTEND_ORIGIN): string {
    const frontendUrl = process.env.NEXT_PUBLIC_FRONTEND_URL || fallback
    return new URL(frontendUrl).origin
}

/**
 * Prepends optional `basePath` to an app path. Use for `fetch`, `location`,
 * plain-anchor `href`, and `public/` image `src` — Next does not prefix those.
 * Hrefs that are not root-relative (`https:`, `//`, `mailto:`, `tel:`, `#`) and
 * paths already under the basePath are returned unchanged, so it is safe on
 * any link value.
 */
export function appPath(path: string): string {
    if (!path || path === '/') {
        return getBasePath() || '/'
    }
    if (!path.startsWith('/') || path.startsWith('//')) {
        return path
    }
    const basePath = getBasePath()
    if (!basePath) return path
    const afterBasePath = path.startsWith(basePath) ? path.slice(basePath.length) : null
    const alreadyMounted = afterBasePath !== null && (afterBasePath === '' || /^[/?#]/.test(afterBasePath))
    return alreadyMounted ? path : `${basePath}${path}`
}

/** Removes a configured `basePath` prefix for app-route comparisons. */
export function stripBasePath(pathname: string): string {
    const basePath = getBasePath()
    if (!basePath) return pathname || '/'
    if (pathname === basePath || pathname === `${basePath}/`) return '/'
    if (pathname.startsWith(`${basePath}/`)) {
        return pathname.slice(basePath.length) || '/'
    }
    return pathname || '/'
}

/**
 * Absolute public URL for browser redirects: origin + optional basePath + path.
 * `new URL('/login', origin)` would resolve from the origin root and leave out the basePath.
 */
export function getPublicAppUrl(pathWithOptionalQuery: string, origin?: string): URL {
    const resolvedOrigin = new URL(origin ?? getPublicFrontendOrigin()).origin
    const relative = pathWithOptionalQuery.startsWith('/')
        ? pathWithOptionalQuery
        : `/${pathWithOptionalQuery}`
    const parsed = new URL(relative, 'https://placeholder.invalid')
    const result = new URL(resolvedOrigin)
    result.pathname = appPath(parsed.pathname)
    result.search = parsed.search
    result.hash = parsed.hash
    return result
}
